import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLogin } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/api/client';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';

interface FormErrors {
  email?: string;
  password?: string;
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as { from?: Location; notice?: string } | null;
  const from = locationState?.from?.pathname ?? '/';
  // e.g. "password changed" after a reset
  const notice = locationState?.notice;

  function validate(): boolean {
    const next: FormErrors = {};
    if (!email.trim()) next.email = 'E-pošta je obvezna.';
    else if (!/^\S+@\S+\.\S+$/.test(email)) next.email = 'Vnesite veljaven e-poštni naslov.';
    if (!password) next.password = 'Geslo je obvezno.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    try {
      await login.mutateAsync({ email, password });
      navigate(from, { replace: true });
    } catch {
      // surfaced via login.error below
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold text-fg">Prijava v svoj račun</h1>

      {notice && <Alert variant="success">{notice}</Alert>}
      {login.isError && <Alert variant="error">{getApiErrorMessage(login.error)}</Alert>}

      <Input
        label="E-pošta"
        type="email"
        name="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
      />
      <Input
        label="Geslo"
        type="password"
        name="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
      />

      <div className="-mt-2 text-right">
        <Link to="/forgot-password" className="text-sm text-fg-muted hover:text-fg hover:underline">
          Pozabljeno geslo?
        </Link>
      </div>

      <Button type="submit" isLoading={login.isPending} className="mt-2 w-full">
        Prijava
      </Button>

      <p className="text-center text-sm text-fg-muted">
        Nimate računa? Ustvarite ga prek povezave na tekmovanje, na katerega se prijavljate.
      </p>
    </form>
  );
}
